import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useFontScale } from '@/contexts/FontScaleContext';
import Button from '@/components/base/Button';
import { useScrollLock } from '@/hooks/useScrollLock';
import FontScaleSelector from './FontScaleSelector';
import { type FontScale } from '@/lib/fontScale';

/**
 * 文字サイズ未設定のユーザーへ、必ず設定させるためのポップアップ。
 * - ログイン済みかつ font_scale が未設定のときだけ表示する（閉じる操作は用意しない）。
 * - 設定を保存すると configured が true になり自動で消える。
 */
export default function FontScalePrompt() {
  const { user, loading } = useAuth();
  const { configured, saving, error, setScale } = useFontScale();
  const [selected, setSelected] = useState<FontScale | null>(null);
  const [showHint, setShowHint] = useState(false);

  const open = !loading && !!user && !configured;

  // 表示中は背後のスクロールを止める（html / body 両方をロック）
  useScrollLock(open);

  if (!open) return null;

  const handleConfirm = async () => {
    if (!selected) {
      setShowHint(true);
      return;
    }
    await setScale(selected);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        <div className="px-5 sm:px-6 py-5 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0"
              style={{ backgroundColor: 'var(--theme-primary)' }}
            >
              <i className="ri-font-size-2 text-lg"></i>
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900">文字サイズを設定してください</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                使い始める前に、見やすい文字サイズを選んでください。
              </p>
            </div>
          </div>
        </div>

        <div className="px-5 sm:px-6 py-5 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600 mb-4">
            端末のピンチ操作での拡大ができないため、ここで選んだサイズで画面全体（文字やボタンなど）が拡大・縮小されます。あとから画面上部の「文字サイズ」ボタンでいつでも変更できます。
          </p>
          <FontScaleSelector
            value={selected}
            onChange={(scale) => {
              setSelected(scale);
              setShowHint(false);
            }}
            disabled={saving}
          />
          {showHint && !selected && (
            <p className="mt-3 text-sm text-red-500">文字サイズを1つ選んでください。</p>
          )}
          {error && <p className="mt-3 text-sm text-red-500">{error}</p>}
        </div>

        <div className="px-5 sm:px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end shrink-0">
          <Button variant="primary" onClick={handleConfirm} disabled={saving}>
            {saving ? '設定中...' : 'このサイズで始める'}
          </Button>
        </div>
      </div>
    </div>
  );
}